// Pagina pubblica /privacy/delete — esercita diritto cancellazione (Sprint 7).

import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AudiqWordmark, RuleBicolor } from '@audiq/ui';
import { LangSwitcher } from '../components/LangSwitcher';

type State = 'idle' | 'submitting' | 'success' | 'not_found' | 'error';

export function PrivacyDeletePage() {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [state, setState] = useState<State>('idle');
  const [summary, setSummary] = useState<{
    encounters_deleted: number;
    messages_deleted: number;
  } | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!identifier.trim() || !confirm) return;
    setState('submitting');
    setErrorText(null);
    const trimmed = identifier.trim();
    const isPickupCode = /^[A-Z0-9]{2,4}-[A-Z0-9]{2,6}$/i.test(trimmed) || trimmed.length <= 8;
    const body = isPickupCode
      ? { pickup_code: trimmed, confirm: true }
      : { pseudonym: trimmed, confirm: true };
    try {
      const res = await fetch('/api/privacy/delete-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 404) {
        setState('not_found');
        return;
      }
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        setState('error');
        setErrorText(j.error ?? 'Errore sconosciuto');
        return;
      }
      const j = (await res.json()) as {
        ok: boolean;
        summary: { encounters_deleted: number; messages_deleted: number };
      };
      setSummary(j.summary);
      setState('success');
    } catch (err) {
      setState('error');
      setErrorText(err instanceof Error ? err.message : 'Errore di rete');
    }
  }

  return (
    <div className="min-h-dvh" style={{ background: 'var(--surface)', color: 'var(--ink)' }}>
      <header className="flex items-center justify-between p-4">
        <Link to="/privacy">
          <AudiqWordmark size="sm" />
        </Link>
        <LangSwitcher />
      </header>

      <main className="mx-auto max-w-xl p-6">
        <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
          {t('privacy.delete.kicker', 'Cancellazione dati')}
        </div>
        <h1 className="serif text-3xl">{t('privacy.delete.title', 'Cancella i tuoi dati')}</h1>
        <RuleBicolor className="my-5 max-w-[10rem]" />
        <p className="mb-6 text-base leading-relaxed" style={{ color: 'var(--ink)' }}>
          {t(
            'privacy.delete.intro',
            "Tutti i dati che Audiq ha su di te verranno cancellati definitivamente. Questa azione non è reversibile. Audit immutabile mantiene solo l'evento di cancellazione (numeri, niente identità).",
          )}
        </p>

        {state === 'success' ? (
          <div
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <p style={{ color: 'var(--ink)' }}>
              {t(
                'privacy.delete.successText',
                'Cancellazione completata. I tuoi dati non sono più recuperabili.',
              )}
            </p>
            {summary && (
              <ul className="mt-3 text-sm" style={{ color: 'var(--mute)' }}>
                <li>
                  {t('privacy.delete.encountersDeleted', 'Conversazioni cancellate')}:{' '}
                  {summary.encounters_deleted}
                </li>
                <li>
                  {t('privacy.delete.messagesDeleted', 'Messaggi cancellati')}:{' '}
                  {summary.messages_deleted}
                </li>
              </ul>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={t('privacy.delete.placeholder', 'pseudonym o codice di pickup') ?? ''}
              className="w-full rounded-md border px-3 py-3"
              style={{
                background: 'var(--paper)',
                borderColor: 'var(--line)',
                color: 'var(--ink)',
              }}
            />
            <label className="flex items-start gap-2 text-sm" style={{ color: 'var(--ink)' }}>
              <input
                type="checkbox"
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
                className="mt-1"
              />
              <span>
                {t(
                  'privacy.delete.confirmLabel',
                  'Confermo: voglio cancellare definitivamente tutti i miei dati. Questa azione non è reversibile.',
                )}
              </span>
            </label>
            <button
              type="submit"
              disabled={!confirm || state === 'submitting'}
              className="rounded-md px-5 py-3 text-sm font-medium"
              style={{
                background: 'var(--danger)',
                color: '#fff',
                opacity: !confirm || state === 'submitting' ? 0.5 : 1,
              }}
            >
              {state === 'submitting'
                ? t('privacy.delete.loading', 'Cancellazione…')
                : t('privacy.delete.submit', 'Cancella tutto')}
            </button>
            {state === 'not_found' && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {t('privacy.delete.notFound', 'Nessun dato trovato per questo identificativo.')}
              </p>
            )}
            {state === 'error' && errorText && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {errorText}
              </p>
            )}
          </form>
        )}

        <div className="mt-10 text-sm" style={{ color: 'var(--mute)' }}>
          <Link to="/privacy" className="underline">
            ← {t('privacy.back', 'Torna ai diritti')}
          </Link>
        </div>
      </main>
    </div>
  );
}
